import * as React from 'react'
import { getAuthUrl } from '../../../../../domutils/utils'
import { LuruReduxStore, RootState } from '../../../../../app/store'
import { connect, ConnectedProps } from 'react-redux'
import { UserMiddleware } from '../../../../../features/user/middleware'
import ModalScreen from '../../../../../primitives/ModalScreen'
import { WorkflowsMiddleware } from '../../../../../features/workflows/middleware'
import { ConnectLuruExtensionsProps, EntityStatus } from '../../../../../app/types'
import { WorkflowState } from '../../../../../features/workflows/types'
import IntegrationCard from '../IntegrationCard'
import slackLogo from '../../../../../images/sor/slack/slack_logo.png'
import CrmRecord from '../../../../../domain/crmRecord/CrmRecord'
import LuruUser from '../../../../../domain/users/LuruUser'
import { MessagingProvider } from '../../../../../features/user/types'
import Utils from '@/utils/Utils'
import { ToastId } from '@/app_ui/types'

function getSlackStatusFromRedux(state: RootState) {
  return {
    workflowsWithActiveState: Object.entries(state?.workflows?.entities).filter(
      ([, wf]) => wf.data.state === WorkflowState.ACTIVE
    ),
    workflowStatus: state.workflows.status,
  }
}

const connector = connect(getSlackStatusFromRedux, null, null, {
  forwardRef: true,
})

export interface SlackCardRefs {
  disconnectModalRef: React.RefObject<ModalScreen>
}

export interface SlackCardProps extends ConnectedProps<typeof connector>, ConnectLuruExtensionsProps {}

interface SlackCardState {
  disconnecting: boolean
}

class SlackCard extends React.Component<SlackCardProps, SlackCardState> {
  props: SlackCardProps
  state: SlackCardState
  componentRefs: SlackCardRefs

  constructor(props: SlackCardProps) {
    super(props)
    this.props = props
    this.state = {
      disconnecting: false,
    }
    this.componentRefs = {
      disconnectModalRef: React.createRef(),
    }
  }

  componentDidMount(): void {
    if (this.props.workflowStatus !== EntityStatus.Loaded) {
      LuruReduxStore.dispatch(WorkflowsMiddleware.listWorkflows.action({}))
    }
  }

  connectToSlack = () => {
    // trackEvent('calendar-connect', 'connect', 'slack')

    // Add redirect path so that we end up at the exact page we are in now
    var path = window === window.top ? window.location.pathname ?? '/' : '/'
    var slackConnectUrl = `${getAuthUrl()}/login/slack/?next=/next${path}`

    if (window === window.top) {
      window.location.href = slackConnectUrl
    } else {
      window.open(slackConnectUrl)
    }
  }

  onClickDisconnectSlack = () => {
    this.componentRefs.disconnectModalRef.current?.showModal({
      ok: () => {
        this.setState({ disconnecting: true })
        this.props.toast.showToast({
          id: ToastId.SETTING_INTEGRATION_TOAST_ID,
          message: 'Disconnecting',
          isLoading: true,
        })
        LuruReduxStore.dispatch(UserMiddleware.disconnectSlack.action({}))
          .unwrap()
          .then(() => {
            this.props.toast.showToast({
              id: ToastId.SETTING_INTEGRATION_TOAST_ID,
              message: 'Disconnected Slack',
              severity: 'success',
            })
            this.setState({ disconnecting: false })
          })
          .catch((err: any) => {
            this.props.toast.showToast({
              id: ToastId.SETTING_INTEGRATION_TOAST_ID,
              message: 'Error disconnecting Slack',
              severity: 'error',
            })
            this.setState({ disconnecting: false })
          })
      },
    })
  }

  render = () => {
    const messagingProvider = LuruUser.getCurrentUserMessagingProviderName()
    const isSlackAdminConnected = LuruUser.isSlackAdminConnected()
    return (
      <>
        <IntegrationCard
          sor='Slack'
          heading='Slack'
          body={`Automate your workflows, configure bi-directional alerts and manage ${CrmRecord.getCrmName()} right from within Slack`}
          logoPath={slackLogo}
          isConnected={messagingProvider === MessagingProvider.SLACK}
          disableConnect={
            messagingProvider === MessagingProvider.GCHAT ||
            messagingProvider === MessagingProvider.MSTEAMS ||
            isSlackAdminConnected
          }
          handleConnect={this.connectToSlack}
          handleDisconnect={this.onClickDisconnectSlack}
          height='15em'
        />
        <ModalScreen title='Disconnect Slack' ref={this.componentRefs.disconnectModalRef}>
          <span
            dangerouslySetInnerHTML={{
              __html:
                this.props.workflowsWithActiveState.length > 0
                  ? `You have ${this.props.workflowsWithActiveState.length} active workflow(s). Disconnecting your Slack account may cause the workflow to not work.<br/>Do you want to continue?`
                  : `Do you want to disconnect from Slack?`,
            }}
          ></span>
        </ModalScreen>
      </>
    )
  }
}

export default React.forwardRef(Utils.connectLuruExtensions(connector(SlackCard)))
